import $http from '@vue-util/http';
import { Cookie } from '@vue-util/cookie';

function uuid() {
  let d = Date.now();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    /* eslint no-bitwise: ["error", { "allow": ["|","&"] }] */
    const r = ((d + (Math.random() * 16)) % 16) | 0;
    d = Math.floor(d / 16);
    return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
  });
}

function getCategoryUrlParams(categoryId, parentCategoryId, topLevelCategoryId) {
  return `&categoryID=${categoryId}${(parentCategoryId !== '') ? `,${parentCategoryId}` : ''}${(topLevelCategoryId !== '') ? `,${topLevelCategoryId}` : ''}`;
}

function getProductData(product) {
  const memberProductMap = product?.relationships?.memberProductMap || '';
  const separator = '%7C';
  const upcCode = [];
  let salePrice = [];
  let inStock = [];
  const sellerNames = [];
  const sellerIDs = [];
  const colorMap = product?.traits?.colors?.colorMap || '';

  // Main product sku, price and instock value
  const parentSku = product?.id || '';

  function getFinalPrice(data) {
    const tieredPrice = data?.pricing?.price?.tieredPrice || '';
    return tieredPrice[tieredPrice.length - 1]?.values[0]?.value || 20;
  }

  const products = product?.detail?.flags?.masterProduct
    ? Object.values(memberProductMap) : [product];
  products.forEach((memberProduct) => {
    const upcData = memberProduct?.relationships?.upcs || {};
    // Child products(based on color) sku, price and instock value
    Object.values(upcData).forEach((upcDataValue) => {
      const seller = upcDataValue?.seller;
      if (seller) {
        sellerNames.push(seller?.sellerName);
        sellerIDs.push(+(seller?.sellerId));
      }
      inStock.push(+(upcDataValue?.availability?.available));
      upcCode.push(+(upcDataValue?.identifier?.upcNumber));
      if (product?.detail?.flags?.consolidatedProductComplex) {
        salePrice.push(getFinalPrice(upcDataValue));
      } else {
        const selectedColor = upcDataValue?.traits?.colors?.selectedColor || '';
        salePrice.push(getFinalPrice(colorMap[selectedColor]));
      }
    });
  });
  const sellerName = sellerNames.join(separator);
  const sellerID = sellerIDs.join(separator);
  const sku = upcCode.join(separator);
  const brandName = product?.detail?.brand?.name || '';
  salePrice = salePrice.join(separator);
  inStock = inStock.join(separator);

  if (!sku) {
    salePrice = 20;
    inStock = 0;
  }
  const productType = product?.detail?.typeName || '';

  return {
    sku,
    brandName,
    salePrice,
    inStock,
    parentSku,
    productType,
    sellerName,
    sellerID,
  };
}

function buildSiteMonUrl(clientId, parameters, device, isBrandShowcase, selectedUpcId) {
  const {
    userId = '', visitorId = '', tagId = '',
  } = parameters.properties;
  let queryParams;
  let siteMonUrl;
  const deviceType = device === 'mobile' ? 'phone' : device;
  if (clientId === 'homepage') {
    siteMonUrl = isBrandShowcase ? `/sitemonetization/v1/brand-showcase/products/sm-hp-${deviceType}` : `/sitemonetization/v1/sponsored-products/sm-hp-${deviceType}`;
    queryParams = `?customerId=${userId}&deviceType=${deviceType}&regionCode=US&sponosoredItemsFilter=ALL&retailer-visitor-id=${visitorId}&tagId=${tagId}&version=1.1`;
    return siteMonUrl.concat(queryParams);
  } if (clientId === 'discovery-xapi') {
    const {
      categoryId = '', parentCategoryId = '', topLevelCategoryId = '', searchTerm = '', isZeroSearchResults = false, pageIndex = '', pageType = '',
    } = parameters;
    queryParams = `?customerId=${userId}&pageIndex=${pageIndex?.value}&deviceType=${deviceType}&regionCode=US&sponosoredItemsFilter=ALL&retailer-visitor-id=${visitorId}&tagId=${tagId}&version=1.1`;
    if ((pageType === 'browse' || (pageType === 'subSplash' && !isBrandShowcase) || (pageType === 'catSplash' && !isBrandShowcase)) && categoryId !== '') {
      siteMonUrl = isBrandShowcase ? `/sitemonetization/v1/brand-showcase/products/sm-browse-${deviceType}` : `/sitemonetization/v1/sponsored-products/sm-browsecarousel-${deviceType}`;
      queryParams += getCategoryUrlParams(categoryId, parentCategoryId, topLevelCategoryId);
      return siteMonUrl.concat(queryParams);
    } if (pageType === 'search' && searchTerm !== '') {
      if (isZeroSearchResults && !isBrandShowcase) {
        siteMonUrl = `/sitemonetization/v1/sponsored-products/sm-zsr-${deviceType}`;
      } else {
        siteMonUrl = isBrandShowcase ? `/sitemonetization/v1/brand-showcase/products/sm-search-${deviceType}` : `/sitemonetization/v1/sponsored-products/sm-searchcarousel-${deviceType}`;
      }
      queryParams += `&keywords=${searchTerm}`;
      return siteMonUrl.concat(queryParams);
    }
  } if (clientId === 'product') {
    const { product = {}, categoryId = '' } = parameters;
    const {
      sku = '',
      parentSku = '',
      salePrice = '',
      inStock = '',
      brandName = '',
      productType = '',
      sellerName = '',
      sellerID = '',
    } = getProductData(product);
    siteMonUrl = `/sitemonetization/v1/sponsored-products/sm-product-${deviceType}`;
    queryParams = `?customerId=${userId}&deviceType=${deviceType}&regionCode=US&item=${sku}&price=${salePrice}&availability=${inStock}${sellerName ? `&sellerName=${sellerName}` : ''}${sellerID ? `&sellerID=${sellerID}` : ''}&categoryID=${categoryId}&sponosoredItemsFilter=ALL&retailer-visitor-id=${visitorId}&tagId=${tagId}&brandName=${brandName}&parentItem=${parentSku}&productType=${productType}${selectedUpcId !== '' ? `&selectedUpcId=${selectedUpcId}` : ''}&version=1.1`;
    return siteMonUrl.concat(queryParams);
  } return '';
}

function getVisitorId(visitorID, separator) {
  if (visitorID.indexOf(separator) !== -1) {
    return decodeURI(visitorID).split(separator)[1].split('|')[1] || '';
  }
  return '';
}

function getCommonUrlParams(isMcom) {
  const userId = isMcom ? Cookie.get('macys_online_guid') : Cookie.get('bloomingdales_online_guid');
  const adobeVisitorId = Cookie.get('AMCV_8D0867C25245AE650A490D4C@AdobeOrg') || '';
  const visitorId = getVisitorId(adobeVisitorId, 'MCMID') || '';
  const utagMain = Cookie.get('utag_main') || ''; // utag_main
  const tagId = (utagMain.indexOf('v_id:') !== -1) ? utagMain.split('v_id:')[1].split('$')[0] : '';
  return {
    userId,
    visitorId,
    tagId,
  };
}

function handleSiteMonResponse(res, isBrandShowcase, clientId) {
  if (res.data && res.status !== 204) {
    const { productPoolMedia } = isBrandShowcase ? res.data.brandShowCase : res.data;
    const { collection } = productPoolMedia;
    if (productPoolMedia && collection.length > 0) {
      if (isBrandShowcase) {
        const { banner, mediaType } = res.data.brandShowCase;
        const isBSProductCountInRange = clientId === 'homepage' ? collection.length >= 4 : collection.length >= 3;
        if (isBSProductCountInRange) {
          if (banner && mediaType === 'banner') {
            return res.data;
          }
          return 'no banner media';
        }
        return 'invalid number of sponsored items';
      }
      return res.data;
    }
    return 'no sponsored items';
  }
  if (res.status === 204) return 'siteMonetization-xapi call did not return any products';
  return 'siteMonetization-xapi call failed';
}

async function fetchSiteMonResponse(clientId, parameters, device, isBrandShowcase, selectedUpcId, sponsoredBrandResponse) {
  if (sponsoredBrandResponse) return sponsoredBrandResponse;
  parameters.properties = getCommonUrlParams(parameters.isMcom);
  const url = buildSiteMonUrl(clientId, parameters, device, isBrandShowcase, selectedUpcId);
  const macysRequestId = uuid();
  try {
    const response = await $http.get(url, {
      responseType: 'json',
      headers: {
        'X-Macys-ClientId': clientId,
        'X-Macys-RequestId': macysRequestId,
      },
    });
    return handleSiteMonResponse(response, isBrandShowcase, clientId);
  } catch (error) {
    return error.message;
  }
}

export {
  fetchSiteMonResponse,
  handleSiteMonResponse,
  getCommonUrlParams,
  buildSiteMonUrl,
  uuid,
  getCategoryUrlParams,
  getProductData,
};
